const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';

const RECAPTCHA_URL = 'https://recaptcha.net/recaptcha/enterprise.js';

export const RecaptchaAccountAction = {
  Login: 'LOGIN',
  Registration: 'REGISTRATION',
  SecurityQuestionChange: 'SECURITY_QUESTION_CHANGE',
  PasswordReset: 'PASSWORD_RESET',
  PhoneNumberUpdate: 'PHONE_NUMBER_UPDATE',
  EmailUpdate: 'EMAIL_UPDATE',
  AccountUpdate: 'ACCOUNT_UPDATE',
  TriggerMfa: 'TRIGGER_MFA',
  RedeemCode: 'REDEEM_CODE',
  ListPaymentMethods: 'LIST_PAYMENT_METHODS',
  AddNewPaymentMethod: 'ADD_NEW_PAYMENT_METHOD'
} as const;

export type RecaptchaActionName =
  | (typeof RecaptchaAccountAction)[keyof typeof RecaptchaAccountAction]
  | 'cart_view'
  | 'checkout'
  | 'account_profile'
  | 'library'
  | 'search'
  | 'product_detail'
  | 'page_view';

export type RecaptchaActionPayload = {
  captchaAction: RecaptchaActionName;
  captchaInputText: string;
};

let recaptchaScriptLoadPromise: Promise<void> | undefined;

const getExistingRecaptchaScript = (key: string) => {
  const scriptById = document.getElementById(RECAPTCHA_SCRIPT_ID);

  if (scriptById instanceof HTMLScriptElement) return scriptById;

  return Array.from(document.scripts).find(script => {
    try {
      const scriptUrl = new URL(script.src);

      return (
        scriptUrl.origin === new URL(RECAPTCHA_URL).origin &&
        scriptUrl.pathname === new URL(RECAPTCHA_URL).pathname &&
        scriptUrl.searchParams.get('render') === key
      );
    } catch {
      return false;
    }
  });
};

export function loadRecaptchaScript(key: string): [id: string, promise: Promise<void>] {
  const id = RECAPTCHA_SCRIPT_ID;

  if (!key) return [id, Promise.reject(new Error('Missing reCAPTCHA site key.'))];

  if (isRecaptchaLoaded()) return [id, Promise.resolve()];

  const existingScript = getExistingRecaptchaScript(key);
  if (existingScript) {
    existingScript.id = id;

    if (recaptchaScriptLoadPromise) {
      return [id, recaptchaScriptLoadPromise];
    }

    return [
      id,
      (recaptchaScriptLoadPromise = new Promise<void>((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => {
            recaptchaScriptLoadPromise = undefined;
            reject(new Error('Failed to load the reCAPTCHA script.'));
          },
          { once: true }
        );
      }))
    ];
  }

  const recaptchaUrl = new URL(RECAPTCHA_URL);
  recaptchaUrl.searchParams.set('render', key);

  const script = document.createElement('script');
  script.id = id;
  script.src = recaptchaUrl.href;
  script.async = true;
  script.defer = true;

  return [
    id,
    (recaptchaScriptLoadPromise = new Promise<void>((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => {
        console.error('Failed to load the reCAPTCHA script.');
        script.remove();
        recaptchaScriptLoadPromise = undefined;
        reject(new Error('Failed to load the reCAPTCHA script.'));
      };
      document.head.appendChild(script);
    }))
  ];
}

export function removeRecaptchaScript(scriptId = RECAPTCHA_SCRIPT_ID) {
  const script = document.getElementById(scriptId);

  if (script) {
    script.remove();
    recaptchaScriptLoadPromise = undefined;
  } else console.warn('Attempted to remove reCAPTCHA script, but it was not found.');
}

function recaptchaReady(): Promise<void> {
  return new Promise<void>(res => {
    window.grecaptcha!.enterprise!.ready(() => res());
  });
}

export async function getRecaptchaToken(key: string, action: string) {
  if (!isRecaptchaLoaded()) {
    const [, promise] = loadRecaptchaScript(key);
    await promise;
  }

  try {
    await recaptchaReady();
    const token = await window.grecaptcha!.enterprise.execute(key, { action });

    return token;
  } catch (err) {
    const error = new Error('Failed to execute reCAPTCHA.', { cause: err });
    console.error(error);
    throw error;
  }
}

export async function getRecaptchaActionPayload(
  action: RecaptchaActionName,
  key = import.meta.env.VITE_RECAPTCHA_SITE_KEY
): Promise<RecaptchaActionPayload> {
  return {
    captchaAction: action,
    captchaInputText: await getRecaptchaToken(key, action)
  };
}

export async function reportRecaptchaAction(
  action: RecaptchaActionName,
  key = import.meta.env.VITE_RECAPTCHA_SITE_KEY
): Promise<void> {
  await getRecaptchaToken(key, action);
}

export function getRecaptchaHeaders(payload: RecaptchaActionPayload): Record<string, string> {
  return {
    'x-recaptcha-action': payload.captchaAction,
    'x-recaptcha-token': payload.captchaInputText
  };
}

export function isRecaptchaLoaded() {
  return !!window.grecaptcha?.enterprise?.execute;
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}
